import { html, LitElement, css, unsafeCSS } from 'lit';
import styles from './tabGroup.scss?inline';
import { ATTRIBUTES, EVENTS } from './constants';
import debounce from 'lodash/debounce';

/**
 * @attribute offset-element - In case tabs are used as an anchors, this attribute contains
 * the CSS selector of an element whose height will be used as an offset when scrolling to this tab group
 */
export default class SotbitTabGroup extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        activeTabIndex: { type: Number, reflect: true, attribute: ATTRIBUTES.ACTIVE_TAB },
        useHistory: { type: Boolean, attribute: ATTRIBUTES.USE_HISTORY },
    };

    constructor() {
        super();
        this.tabs = [];
        this.panels = [];
        this.underline = null;
        this.activeTabIndex = 0;
        this.useHistory = false;

        this._handleSlotChangeDebonce = debounce(this._handleSlotChange.bind(this), 30, {
            maxWait: 30,
            leading: false,
            trailing: true,
        });
    }

    firstUpdated() {
        const container = this.shadowRoot.querySelector('.tabs');
        container.addEventListener(EVENTS.SCROLL, () => this._updateUnderlineOnScroll());
    }

    updated(changedProps) {
        if (changedProps.has('activeTabIndex') && typeof changedProps.get('activeTabIndex') === 'number') {
            this.setActiveTab(this.activeTabIndex);
        }
    }

    _handleSlotChange() {
        this.tabs = this.shadowRoot.querySelector('slot[name="tab"]').assignedElements({ flatten: true });
        this.panels = this.shadowRoot.querySelector('slot:not([name])').assignedElements({ flatten: true });

        this.tabs.forEach((tab) => {
            if (tab._handleClick) {
                tab.removeEventListener(EVENTS.CLICK, tab._handleClick);
            }
        });

        this.tabs.forEach((tab, index) => {
            tab._handleClick = () => {
                if (!tab.disabled) {
                    this.activeTabIndex = index;
                }

                if (tab.id && this.useHistory) {
                    window.history.replaceState(null, '', `#${tab.id}`);
                }
            };

            tab.addEventListener(EVENTS.CLICK, tab._handleClick);
        });

        requestAnimationFrame(() => {
            const initiallyActiveIndex = this.tabs.findIndex((tab) => tab.hasAttribute('active'));
            this.activeTabIndex = initiallyActiveIndex !== -1 ? initiallyActiveIndex : this.activeTabIndex;

            if (!this.underline) {
                this._createUnderline();
            }

            if (!this.useHistory) {
                this.setActiveTab(this.activeTabIndex, { isInitial: true });
                return;
            }

            for (const [index, tab] of this.tabs.entries()) {
                if (tab.id && window.location.hash?.replace('#', '') === tab.id) {
                    this.setActiveTab(index, { isInitial: true });

                    // scroll window to the top edge of the tab group
                    setTimeout(() => {
                        const top = this.getBoundingClientRect().top + window.scrollY;
                        const offsetElement = document.querySelector(this.getAttribute('offset-element'));

                        let offset = 0;

                        if (offsetElement) {
                            offset = offsetElement.offsetHeight;
                        }

                        window.scrollTo({ top: top - offset, behavior: 'smooth' });
                    }, 50);
                    return;
                }
            }

            this.setActiveTab(this.activeTabIndex, { isInitial: true });
        });
    }

    _createUnderline() {
        if (this.underline) {
            return;
        }

        this.underline = document.createElement('div');
        this.underline.className = 'underline';
        this.shadowRoot.querySelector('.tabs').appendChild(this.underline);
    }

    _moveUnderline(tab, options = {}) {
        if (!tab || !this.underline) {
            return;
        }

        const container = this.shadowRoot.querySelector('.tabs');
        const tabRect = tab.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const TRANSITION_VALUE = 'transform 0.3s ease, width 0.3s ease';

        if (tabRect.width === 0) {
            return;
        }

        const offsetX = tabRect.left - containerRect.left + container.scrollLeft;
        const useTransition = !options.isInitial && !options.isScrolling;

        this.underline.style.transition = useTransition ? TRANSITION_VALUE : 'none';
        this.underline.style.width = `${tabRect.width}px`;
        this.underline.style.transform = `translateX(${offsetX}px)`;
    }

    setActiveTab(selectedIndex, options = {}) {
        if (!this.tabs.length && !this.panels.length) {
            return;
        }

        this.activeTabIndex = selectedIndex;

        if (this.tabs.length) {
            this.tabs.forEach((tab, index) => {
                tab.active = index === selectedIndex;
            });
        }

        if (this.panels.length) {
            this.panels.forEach((panel, index) => {
                panel.active = index === selectedIndex;
            });
        }

        const activeTab = this.tabs[selectedIndex];

        if (activeTab) {
            this._moveUnderline(activeTab, options);
        }

        if (!options.isInitial) {
            const event = new CustomEvent(EVENTS.CHANGE, {
                bubbles: true,
                composed: true,
                detail: {
                    index: selectedIndex,
                    tab: activeTab,
                    name: 'tabChange',
                },
            });

            this.dispatchEvent(event);
        }
    }

    _updateUnderlineOnScroll() {
        const activeTab = this.tabs.find((tab) => tab.active);

        if (activeTab) {
            this._moveUnderline(activeTab, { isScrolling: true });
        }
    }

    render() {
        return html`
            <div class="tab-group" part="tab-group" @slotchange="${this._handleSlotChangeDebonce}">
                <div class="tabs" part="tabs">
                    <slot name="tab"></slot>
                </div>

                <div class="panels" part="panels">
                    <slot></slot>
                </div>
            </div>
        `;
    }
}
